<template>
  <div
    class="flex items-center justify-center sidebar-toggle-wrap"
    :class="[
      { 'electron-drag': !isCardOpen && !showPeekSidebar },
      { 'pl-18': isElectronApp && !isSidebar },
    ]"
    :style="
      sidebarExpanded && !showPeekSidebar
        ? {
            marginLeft: '37px',
            padding: '0',
            marginRight: '0',
            transform: 'translateY(0)',
          }
        : {}
    "
    @mouseleave="mouseLeaveHandler"
  >
    <v-popover
      tag="div"
      content-tag="div"
      :on-show="() => !isMobile"
      :style="{
        marginRight: sidebarExpanded && !showPeekSidebar ? '0' : '8px',
        height: '24px',
      }"
    >
      <div
        class="sidebar-menu-button pt group hover:bg-icon-bg electron-no-drag"
        :class="{
          'sidebar-close-icon': !sidebarExpanded || (sidebarExpanded && showPeekSidebar),
          'hover:bg-gray-100': theme === $constants.light,
        }"
        @touchstart.prevent.stop="toggleSidebar"
        @click="toggleSidebar"
        @mouseenter="mouseEnterHandler"
        @mouseleave="hoverIcon = false"
      >
        <NotificationCount
          v-if="showNotificationCount"
          class="me-notification"
          :totalcount="notificationCount"
          :small-notification="isMobile"
        />
        <em
          class="custom-icons sidebar-icon w-3 h-3 bg-icon-default group-hover:bg-icon-hover"
          :style="[getCustomIconStyle(iconPath(sidebarToggleIcon))]"
        />
      </div>
      <template #content="{ state }">
        <TooltipTemplate
          v-if="state.isVisible"
          :tooltip-text="tooltipContent"
          :tooltip-meta="{ icon: { ctrl: true, key: '\\' } }"
        />
      </template>
    </v-popover>
  </div>
</template>

<script>
import {
  setRootStyle,
  convertStyleToNumber,
  getDefaultSidebarWidth,
  getSidebarWidth,
} from '@/utilities/rootStyles';
import EventBus from '@/utilities/eventBus';
import customIconsMixin from '@/mixins/customIconsMixin';
import NotificationCount from '@/components/widgets/NotificationCount';
import TooltipTemplate from '@/components/elements/TooltipTemplate';
import isCardMixin from '@/mixins/isCardMixin';
import useIsElectronApp from '@/utilities/composables/useIsElectronApp';

const defaultWidth = getDefaultSidebarWidth();

export default {
  name: 'SidbarToggle',

  components: {
    NotificationCount,
    TooltipTemplate,
  },

  mixins: [customIconsMixin, isCardMixin],

  props: {
    isSidebar: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  setup() {
    const { isElectronApp } = useIsElectronApp();
    return { isElectronApp };
  },

  data() {
    return {
      hoverIcon: false,
    };
  },

  computed: {
    tooltipContent() {
      return this.sidebarExpanded && this.showPeekSidebar
        ? this.translate('lockSidebar')
        : this.translate('hideSidebar');
    },
    showPeekSidebar() {
      return this.$store.getters.showPeekSidebar || false;
    },
    sidebarExpanded() {
      return this.$store.getters.sidebarExpanded;
    },
    sidebarToggleIcon() {
      const collapsedIcon = this.isMobile ? 'menu' : 'chevrons-right';
      return this.sidebarExpanded && !this.showPeekSidebar ? 'chevrons-left' : collapsedIcon;
    },
    notificationCount() {
      return this.$store.getters.notificationCount;
    },
    showNotificationCount() {
      return this.notificationCount && this.isMobile && !this.sidebarExpanded && !this.isInbox;
    },
    isInbox() {
      return this.$route.name === this.$constants.routeNames.inbox;
    },
    theme() {
      return this.$store.getters.getTheme;
    },
  },

  mounted() {
    if (this.isMobile) {
      EventBus.$on('hideSidebar', this.hideSidebar);
    }
  },

  unmounted() {
    if (this.isMobile) {
      EventBus.$off('hideSidebar', this.hideSidebar);
    }
  },
  methods: {
    mouseLeaveHandler(e) {
      const excludedClassNames = ['sidebar', 'peek', 'tooltip'];
      const classNames = e.toElement?.className ?? '';
      if (excludedClassNames.some((className) => classNames.includes(className))) {
        return;
      }
      if (e.toElement?.className?.includes('sidebar') || e.toElement?.className?.includes('peek'))
        return;
      EventBus.$emit('closeSidebar', e);
    },
    mouseEnterHandler() {
      this.hoverIcon = true;
      if (this.isMobile) return;
      EventBus.$emit('openSidebar');
    },
    toggleSidebar() {
      if (this.sidebarExpanded && this.showPeekSidebar) {
        EventBus.$emit('toggledSidebar', true);
        return;
      }
      this.$store.dispatch('setSidebarExpanded', !this.sidebarExpanded);
      if (!this.sidebarExpanded) {
        EventBus.$emit('toggledSidebar', false);

        const sidebarWidth = getSidebarWidth();
        if (sidebarWidth !== 0) {
          localStorage.setItem('sidebarExpandWidth', sidebarWidth);
        }
        this.setSidebarStyle(0);
        return;
      }
      const width =
        localStorage.getItem('sidebarExpandWidth') || convertStyleToNumber(defaultWidth);
      this.setSidebarStyle(width);
      EventBus.$emit('toggledSidebar', true);
    },

    hideSidebar() {
      this.$store.dispatch('setSidebarExpanded', false);
      this.setSidebarStyle(0);
    },

    setSidebarStyle(width) {
      setRootStyle(this.$constants.sidebarWidthRoot, `${width}px`);
      localStorage.setItem('sidebarWidth', width);
    },
  },
};
</script>

<style lang="scss">
.sidebar-menu-button {
  display: flex;
  justify-content: center;
  user-select: none;
  width: 24px;
  height: 24px;
  align-items: center;
  border-radius: 4px;
  margin-left: auto;
  &:hover {
    background-color: var(--iconBg) !important;
  }
  &.sidebar-close-icon {
    &:hover {
      background-color: $gray100;
    }
  }
}

.mobile {
  .sidebar-menu-button {
    position: relative;
  }

  .card-header-wrap,
  .header-wrap {
    .sidebar-icon {
      display: flex;
      justify-content: center;
      width: 20px;
      height: 20px;
    }
  }
}
.sidebar-toggle-wrap {
  padding-bottom: 10px;
  padding-left: 16px;
  padding-right: 30px;
  margin-right: -30px;
  margin-left: -16px;
  transform: translateY(5px);
}
</style>
